import { addDays, endOfMonth, endOfYear } from 'date-fns'

import { fromDateStr, toDateStr } from '@/utils/date-str'

/**
 * Reports has a wider union than the dashboard/partner-detail shared
 * `Granularity` (it adds 'yearly'), so this type and its predicate live here
 * rather than force a merged 4-value type onto the simpler surfaces
 * (see `granularity.ts` for the 3-value owner).
 */
export type ReportGranularity = 'daily' | 'weekly' | 'monthly' | 'yearly'

export const REPORT_GRANULARITIES = ['daily', 'weekly', 'monthly', 'yearly'] as const

export const isReportGranularity = (v: string): v is ReportGranularity =>
  (REPORT_GRANULARITIES as readonly string[]).includes(v)

/**
 * The [from, to] calendar window a report row covers, derived from its
 * period_start + the active granularity. Used to drill from a period row
 * into /admin/orders filtered to exactly the orders that period counted
 * (both sides filter on the payment date).
 *
 * The weekly branch (+6 days) assumes the BE buckets weeks as 7 days
 * anchored on `period_start` — pinned by report-period.test.ts so a BE
 * bucketing change surfaces as a test failure, not a silent off-by-one
 * in the drill-down filter.
 */
export const periodWindow = (
  periodStart: string,
  granularity: ReportGranularity,
): [string, string] => {
  const start = fromDateStr(periodStart)
  const end =
    granularity === 'daily'
      ? start
      : granularity === 'weekly'
        ? addDays(start, 6)
        : granularity === 'monthly'
          ? endOfMonth(start)
          : endOfYear(start)

  return [toDateStr(start), toDateStr(end)]
}
